import { STEP_RECORDING_STATUS } from '../../components/AutomationStudio/utils/replayEligibility';
import { REPLAY_STEP_STATUS, RUN_STATUS } from './constants';

/** Canonical recording tokens — values are snake_case (e.g. failed_by_evaluation). */
const RECORDING_STATUS = new Set(Object.values(STEP_RECORDING_STATUS));

function isRecordingPlanStep(step) {
    if (!step || step.customStep === true) return false;
    if (typeof step.replayable === 'boolean') return true;
    return RECORDING_STATUS.has(step.status);
}

function isRecordedPlanSnapshot(instructions = []) {
    const steps = instructions.flatMap((inst) => inst.aiSteps ?? []);
    if (steps.length === 0) return false;
    const hasRecordingMarker = steps.some(isRecordingPlanStep);
    const hasRuntimeStatus = steps.some((step) => step?.status === 'success' || step?.status === 'running');
    return hasRecordingMarker && !hasRuntimeStatus;
}

function maskLoadedStepStatus(step, preserveRecordingStatus) {
    if (
        preserveRecordingStatus &&
        isRecordingPlanStep(step) &&
        RECORDING_STATUS.has(step.status)
    ) {
        return step.status;
    }
    return 'pending';
}

/**
 * Plan helpers for AutomationStudioNew normalized state.
 */

export function filterActiveInstructions(instructions = []) {
    return (instructions || []).filter((inst) => !inst.removedFromEditor);
}

/**
 * Whether the instruction editor may add a row after `index` (QPN-2017).
 * Skips soft-removed rows and blocks stacking another blank below a visible blank/empty neighbor.
 */
export function canAddInstructionBelow(instructions = [], index) {
    const list = instructions || [];
    const current = list[index];
    if (!current || current.removedFromEditor) return false;
    if (!String(current.text ?? '').trim()) return false;

    const nextVisible = list
        .slice(index + 1)
        .find((inst) => !inst?.removedFromEditor);
    if (!nextVisible) return true;
    return String(nextVisible.text ?? '').trim().length > 0;
}

export function getAddInstructionBelowTooltip(instructions = [], index) {
    const list = instructions || [];
    const current = list[index];
    if (!current || current.removedFromEditor) return null;
    if (!String(current.text ?? '').trim()) {
        return 'Fill current step to add another';
    }
    if (!canAddInstructionBelow(list, index)) {
        return 'Fill the step below before adding another';
    }
    return null;
}

export function clonePlanSnapshot({ instructions, dynamicResult } = {}) {
    return {
        instructions: JSON.parse(JSON.stringify(instructions ?? [])),
        dynamicResult:
            dynamicResult != null ? JSON.parse(JSON.stringify(dynamicResult)) : null,
    };
}

export function captureLeftPanelGroupSnapshot(instructions = []) {
    return clonePlanSnapshot({ instructions }).instructions;
}

/** Freeze load-time group membership; merge live steps only for groups that had steps at snapshot. */
export function mergeLeftPanelGroupSnapshotWithLiveSteps(snapshot = [], live = []) {
    const liveById = new Map((live || []).map((inst) => [String(inst.id), inst]));
    return (snapshot || [])
        .filter((inst) => !inst.removedFromEditor)
        .map((snapInst) => {
            const liveInst = liveById.get(String(snapInst.id));
            const snapHadSteps = (snapInst.aiSteps?.length ?? 0) > 0;

            if (!snapHadSteps) {
                return { ...snapInst, aiSteps: [] };
            }

            return {
                ...snapInst,
                aiSteps: liveInst?.aiSteps ?? snapInst.aiSteps ?? [],
            };
        });
}

/** Reassign global stepIndex across non-removed instructions only. */
export function reindexPlanStepIndices(instructions = []) {
    let globalIdx = 0;
    return (instructions || []).map((inst) => {
        if (inst.removedFromEditor) {
            return inst;
        }
        return {
            ...inst,
            aiSteps: (inst.aiSteps ?? []).map((step) => ({
                ...step,
                stepIndex: globalIdx++,
            })),
        };
    });
}

export function getFlatSteps(instructions = []) {
    const flat = [];
    for (const inst of instructions) {
        if (!(inst.aiSteps?.length > 0)) continue;
        for (const step of inst.aiSteps) {
            flat.push(step);
        }
    }
    return flat;
}

/** Flat steps for save/replay/export — excludes instructions removed in the editor. */
export function getActiveFlatSteps(instructions = []) {
    const flat = [];
    for (const inst of filterActiveInstructions(instructions)) {
        for (const step of inst.aiSteps ?? []) {
            flat.push(step);
        }
    }
    return flat;
}

export function buildStepIndexToStepId(flatSteps = []) {
    const map = {};
    flatSteps.forEach((step, index) => {
        if (step?.id != null) {
            map[index] = step.id;
        }
    });
    return map;
}

export function findStepById(instructions = [], stepId) {
    if (!stepId) return null;
    for (const inst of instructions) {
        const found = inst.aiSteps?.find((s) => s.id === stepId);
        if (found) return found;
    }
    return null;
}

export function allStepIds(instructions = []) {
    return getFlatSteps(instructions).map((s) => s.id).filter(Boolean);
}

export function getStepIdsBetween(orderedIds, anchorId, targetId) {
    const anchorIndex = orderedIds.indexOf(anchorId);
    const targetIndex = orderedIds.indexOf(targetId);
    if (anchorIndex === -1 || targetIndex === -1) {
        return targetId ? [targetId] : [];
    }
    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    return orderedIds.slice(start, end + 1);
}

export function reconcileSelection(instructions, selection) {
    const universe = new Set(allStepIds(instructions));
    const selectedStepIds = [...new Set(selection.selectedStepIds ?? [])]
        .filter((id) => universe.has(id));
    const selectionAnchorId = selection.selectionAnchorId && universe.has(selection.selectionAnchorId)
        ? selection.selectionAnchorId
        : null;
    const selectedStepId = selection.selectedStepId && universe.has(selection.selectedStepId)
        ? selection.selectedStepId
        : null;
    return { selectedStepIds, selectionAnchorId, selectedStepId };
}

export function hasInstructionText(instructions = []) {
    return instructions.some(
        (inst) => !inst.removedFromEditor && String(inst?.text ?? '').trim().length > 0
    );
}

/**
 * Prepare instruction rows for a new Generate AI run (QPN-2054).
 * Drops editor-removed and empty-text rows, assigns fresh sequential ids, clears aiSteps.
 */
export function prepareInstructionsForGeneration(instructions = []) {
    let nextId = 1;
    return (instructions || [])
        .filter((inst) => !inst.removedFromEditor && String(inst?.text ?? '').trim())
        .map((inst) => ({
            ...inst,
            id: nextId++,
            aiSteps: [],
        }));
}

/** Agent WebSocket payload: { id, text } per prepared instruction. */
export function buildValidInstructionsPayload(preparedInstructions = []) {
    return (preparedInstructions || []).map((inst) => ({
        id: inst.id,
        text: inst.text,
    }));
}

/** True when every instruction id is unique (guards executionFlow / testPlan merge). */
export function hasUniqueInstructionIds(instructions = []) {
    const ids = (instructions || []).map((inst) => String(inst.id));
    return ids.length === new Set(ids).size;
}

export function hasGeneratedSteps(instructions = []) {
    return getFlatSteps(instructions).length > 0;
}

export function instructionsSyncedWithSteps(instructions = []) {
    const withText = filterActiveInstructions(instructions).filter(
        (inst) => String(inst?.text ?? '').trim().length > 0
    );
    if (withText.length === 0) return true;
    return withText.every((inst) => Array.isArray(inst.aiSteps) && inst.aiSteps.length > 0);
}

/**
 * Mask historical runtime fields on loaded saved plans until a fresh run.
 */
export function maskSavedPlanRuntimeFields(instructions = []) {
    const preserveRecordingStatus = isRecordedPlanSnapshot(instructions);
    return instructions.map((inst) => ({
        ...inst,
        aiSteps: (inst.aiSteps ?? []).map((step) => ({
            ...step,
            status: maskLoadedStepStatus(step, preserveRecordingStatus),
            passed: undefined,
            duration: undefined,
            screenshot: undefined,
            screenshotPath: undefined,
            result: undefined,
            resultMessage: undefined,
            error: undefined,
        })),
    }));
}

export function mapReplayCompleteStatus(rawStatus) {
    const s = String(rawStatus ?? '').toLowerCase();
    if (['completed', 'complete', 'success', 'passed'].includes(s)) {
        return REPLAY_STEP_STATUS.PASSED;
    }
    if (['failed', 'failure', 'error'].includes(s)) {
        return REPLAY_STEP_STATUS.FAILED;
    }
    if (['skipped', 'skip'].includes(s)) {
        return REPLAY_STEP_STATUS.SKIPPED;
    }
    if (['executing', 'running', 'in_progress', 'in-progress'].includes(s)) {
        return REPLAY_STEP_STATUS.IN_PROGRESS;
    }
    return REPLAY_STEP_STATUS.PENDING;
}

export function resolveStepIdFromReplayIndex(stepIndex, stepIndexToStepId = {}) {
    if (stepIndex == null || Number.isNaN(Number(stepIndex))) return null;
    const idx = Number(stepIndex);
    return stepIndexToStepId[idx] ?? null;
}

/** User-facing copy when record/generate/replay is stopped before natural completion. */
export const USER_STOPPED_EXECUTION_MESSAGE = 'Execution Aborted by User';

/** @deprecated Use USER_STOPPED_EXECUTION_MESSAGE */
export const GENERATION_ABORTED_MESSAGE = USER_STOPPED_EXECUTION_MESSAGE;

/** True when a stopped generation session still has partial UI artifacts to review. */
export function hasGenerationSessionArtifacts({ generationRun, planDraft } = {}) {
    const hasSteps = getActiveFlatSteps(planDraft?.instructions ?? []).length > 0;
    return (
        hasSteps ||
        (generationRun?.logs?.length ?? 0) > 0 ||
        (generationRun?.screenshots?.length ?? 0) > 0 ||
        generationRun?.executionFlow != null
    );
}

/** True when save follows a completed record/generate session (preserve live execution UI). */
export function shouldResetExecutionSessionOnSave(generationRun) {
    return generationRun?.status === RUN_STATUS.COMPLETED;
}

/** Prefer live plan-draft instructions (editor source of truth).
 * completedInstructions is a generate/record-time freeze — using it on save dropped
 * post-session step edits (generate → edit → save wrote the pre-edit snapshot).
 */
export function resolvePostSessionSaveInstructions(generationRun, planInstructions = []) {
    if (Array.isArray(planInstructions) && planInstructions.length > 0) {
        return clonePlanSnapshot({ instructions: planInstructions }).instructions;
    }
    if (
        Array.isArray(generationRun?.completedInstructions) &&
        generationRun.completedInstructions.length > 0
    ) {
        return clonePlanSnapshot({ instructions: generationRun.completedInstructions }).instructions;
    }
    return [];
}

/**
 * After record/generate → save in the same session, skip API reload when the URL navigates to
 * the testcase we just saved — Redux already holds the live execution UI until a fresh load.
 */
export function shouldSkipSavedPlanReload(studioState, testCaseId) {
    if (!testCaseId) return false;
    const planDraft = studioState?.planDraft;
    const savedPlan = studioState?.savedPlan;
    const generationRun = studioState?.generationRun;
    const sameSavedTestCase =
        savedPlan?.isSaved &&
        savedPlan?.testCaseId != null &&
        String(savedPlan.testCaseId) === String(testCaseId);
    if (!sameSavedTestCase) return false;

    const replayRun = studioState?.replayRun;
    const hasActiveSessionExecution =
        planDraft?.justSavedInSession ||
        planDraft?.keepRecordingExecutionUi ||
        (generationRun?.status === RUN_STATUS.COMPLETED &&
            Array.isArray(generationRun?.completedInstructions) &&
            generationRun.completedInstructions.length > 0);
    // Import → Save & Replay navigates to the new testcase while replay is already running;
    // skip API reload so loadPlanFromTestcase does not wipe replayRun (loader / step progress).
    const hasActiveReplayOnSavedTestCase = replayRun?.status === RUN_STATUS.RUNNING;

    return Boolean(hasActiveSessionExecution || hasActiveReplayOnSavedTestCase);
}

/** Strip prior-run step rows immediately when a new Generate AI session starts. */
export function clearInstructionsForGenerationStart(instructions = []) {
    return (instructions || []).map((inst) => ({
        ...inst,
        aiSteps: [],
    }));
}

/**
 * Clear instruction text in the editor while keeping left-panel step grouping.
 * Instructions that already have steps are hidden from the editor (removedFromEditor)
 * but remain in plan state so the left panel hierarchy is unchanged.
 * A single empty editor row is appended for new instruction text.
 */
export function buildClearedInstructions(instructions = []) {
    const list = instructions || [];
    const withSteps = list.filter((inst) => Array.isArray(inst?.aiSteps) && inst.aiSteps.length > 0);

    if (withSteps.length === 0) {
        return [{ id: Date.now(), text: '', aiSteps: [] }];
    }

    const preservedForLeftPanel = withSteps.map((inst) => ({
        ...inst,
        text: '',
        removedFromEditor: true,
    }));

    return [
        ...preservedForLeftPanel,
        { id: Date.now(), text: '', aiSteps: [] },
    ];
}
